import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";

const MapManagement = () => {
  const { id } = useParams();
  const [mapData, setMapData] = useState({
    url: "",
    projectName: "",
    error: null,
  });
  const [loading, setLoading] = useState(true);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  // Function to convert regular Google Maps URL to embed URL
  const convertToEmbedUrl = (url) => {
    if (!url) return "";

    try {
      console.log("Original URL:", url);

      // If it's already an embed URL, return as is
      if (url.includes("/embed") || url.includes("output=embed")) {
        return url;
      }

      // Clean up the URL first
      let cleanUrl = url.trim();

      // Handle different Google Maps URL formats

      // Format 1: Regular Google Maps URL (e.g., https://goo.gl/maps/... or https://maps.app.goo.gl/...)
      if (
        cleanUrl.includes("goo.gl/maps") ||
        cleanUrl.includes("maps.app.goo.gl")
      ) {
        // These short URLs need to be expanded first
        // For now, we'll try to extract coordinates if available
        const match = cleanUrl.match(/@([-\d.]+),([-\d.]+)/);
        if (match) {
          const lat = match[1];
          const lng = match[2];
          return `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d0!2d${lng}!3d${lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2z${lat}%2C${lng}!5e0!3m2!1sen!2sus!4v1`;
        }
      }

      // Format 2: Direct Google Maps URL with place ID or coordinates
      const urlObj = new URL(cleanUrl);
      const params = new URLSearchParams(urlObj.search);

      // Extract coordinates from URL
      const query = params.get("q") || "";
      const ll = params.get("ll") || "";
      const sll = params.get("sll") || "";

      let lat, lng;

      // Try to extract coordinates from various parameters
      if (ll) {
        [lat, lng] = ll.split(",");
      } else if (sll) {
        [lat, lng] = sll.split(",");
      } else if (query.includes(",")) {
        const coords = query.split(",").map((coord) => coord.trim());
        if (coords.length >= 2) {
          [lat, lng] = coords;
        }
      }

      // If we found coordinates, create embed URL
      if (lat && lng) {
        const embedUrl = `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d0!2d${lng}!3d${lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2z${lat}%2C${lng}!5e0!3m2!1sen!2sus!4v1`;
        console.log("Generated embed URL:", embedUrl);
        return embedUrl;
      }

      // If no coordinates found but it's a Google Maps URL, try to use the place
      if (cleanUrl.includes("google.com/maps/place/")) {
        const placeMatch = cleanUrl.match(/place\/([^/]+)/);
        if (placeMatch) {
          const placeId = placeMatch[1];
          return `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d0!2d0!3d0!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s${placeId}!2s!5e0!3m2!1sen!2sus!4v1`;
        }
      }

      return cleanUrl;
    } catch (error) {
      console.error("Error converting URL:", error);
      return url; // Return original URL if conversion fails
    }
  };

  const fetchMapData = useCallback(async () => {
    if (!id) {
      setMapData((prev) => ({
        ...prev,
        error: "No project ID provided",
      }));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setIframeLoaded(false);

      const response = await fetch(
        `https://dev.mithahomes.com/backend/api/mainprojectprocess/projectsgetapi/${id}`,
        {
          headers: {
            Accept: "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();
      console.log("API Response:", result);

      if (result.success && result.data) {
        let mapUrl = result.data.project_process_stage || "";
        const projectName = result.data.project_name || "Project Location";

        // Validate and convert the URL if needed
        if (mapUrl) {
          mapUrl = convertToEmbedUrl(mapUrl);
          console.log("Processed map URL:", mapUrl);
        }

        setMapData({
          url: mapUrl,
          projectName: projectName,
          error: null,
        });
      } else {
        throw new Error(result.message || "Invalid API response structure");
      }
    } catch (error) {
      console.error("Error fetching map data:", error);
      setMapData((prev) => ({
        ...prev,
        error: error.message || "Failed to load map data",
      }));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchMapData();
  }, [fetchMapData]);

  const handleIframeLoad = () => {
    setIframeLoaded(true);
  };

  const handleIframeError = () => {
    console.error("Iframe failed to load");
    setMapData((prev) => ({
      ...prev,
      error: "Failed to load the map. The URL might be invalid or blocked.",
    }));
    setIframeLoaded(true); // To hide loading spinner
  };

  const handleRetry = () => {
    fetchMapData();
  };

  const openGoogleMaps = () => {
    // Try to convert to a regular Google Maps URL if it's an embed URL
    let mapsUrl = mapData.url;
    if (mapsUrl.includes("/embed")) {
      mapsUrl = mapsUrl.replace("/embed", "/place").split("?")[0];
    }
    window.open(mapsUrl, "_blank", "noopener,noreferrer");
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Loading Project Details...</p>
        </div>
      </div>
    );
  }

  if (mapData.error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorCard}>
          <div style={styles.errorIcon}>⚠️</div>
          <h3 style={styles.errorTitle}>Unable to Load Map</h3>
          <p style={styles.errorMessage}>{mapData.error}</p>
          <p style={styles.errorDetails}>Project ID: {id || "Not specified"}</p>
          <button onClick={handleRetry} style={styles.retryButton}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.mapContainer}>
          {mapData.url ? (
            <>
              {!iframeLoaded && (
                <div style={styles.iframeLoading}>
                  <div style={styles.smallSpinner}></div>
                  <p>Loading map...</p>
                </div>
              )}
              <iframe
                src={mapData.url}
                width="100%"
                height="450"
                style={{
                  ...styles.iframe,
                  opacity: iframeLoaded ? 1 : 0,
                }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={`Google Map - ${mapData.projectName}`}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
                sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
              />
            </>
          ) : (
            <div style={styles.noMap}>
              <div style={styles.noMapIcon}>🗺️</div>
              <h3 style={styles.noMapTitle}>No Map Available</h3>
              <p style={styles.noMapMessage}>
                This project doesn't have a map location configured yet.
              </p>
              <p style={styles.smallText}>Project ID: {id || "N/A"}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Styles (same as before, but with added button styles)
const styles = {
  container: {
    padding: "20px",
    backgroundColor: "#f8fafc",
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    width: "100%",
    maxWidth: "1000px",
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.08)",
    overflow: "hidden",
  },
  mapContainer: {
    position: "relative",
    backgroundColor: "#f1f3f9",
    minHeight: "450px",
  },
  iframe: {
    border: "none",
    display: "block",
    transition: "opacity 0.3s ease",
  },
  iframeLoading: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    textAlign: "center",
    color: "#666",
    zIndex: 1,
  },
  noMap: {
    height: "450px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    color: "#6c757d",
    padding: "40px",
    textAlign: "center",
  },
  noMapIcon: {
    fontSize: "60px",
    marginBottom: "20px",
    opacity: 0.5,
  },
  noMapTitle: {
    fontSize: "24px",
    fontWeight: "600",
    margin: "0 0 10px",
    color: "#495057",
  },
  noMapMessage: {
    fontSize: "16px",
    margin: "0 0 20px",
    maxWidth: "400px",
    lineHeight: "1.5",
  },
  smallText: {
    fontSize: "12px",
    color: "#adb5bd",
    marginTop: "8px",
  },
  footer: {
    padding: "20px 30px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#f8f9fa",
    borderTop: "1px solid #e9ecef",
  },
  mapLink: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "14px",
    color: "#007bff",
    textDecoration: "none",
    fontWeight: "600",
    padding: "10px 20px",
    backgroundColor: "#e7f1ff",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    transition: "all 0.2s ease",
    fontFamily: "inherit",
  },
  linkIcon: {
    fontSize: "18px",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "60vh",
    gap: "20px",
  },
  spinner: {
    width: "50px",
    height: "50px",
    border: "5px solid #f3f3f3",
    borderTop: "5px solid #667eea",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  smallSpinner: {
    width: "30px",
    height: "30px",
    border: "3px solid #f3f3f3",
    borderTop: "3px solid #667eea",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    margin: "0 auto 15px",
  },
  loadingText: {
    fontSize: "16px",
    color: "#666",
    margin: 0,
  },
  errorCard: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "40px",
    textAlign: "center",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    maxWidth: "500px",
    width: "100%",
  },
  errorIcon: {
    fontSize: "60px",
    marginBottom: "20px",
  },
  errorTitle: {
    color: "#dc3545",
    margin: "0 0 10px",
    fontSize: "22px",
  },
  errorMessage: {
    color: "#6c757d",
    margin: "0 0 15px",
    fontSize: "15px",
  },
  errorDetails: {
    color: "#adb5bd",
    fontSize: "13px",
    marginBottom: "25px",
  },
  retryButton: {
    backgroundColor: "#667eea",
    color: "white",
    border: "none",
    padding: "12px 30px",
    borderRadius: "8px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
};

// Add CSS animation
if (typeof document !== "undefined") {
  const styleSheet = document.createElement("style");
  styleSheet.textContent = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    
    button:hover {
      opacity: 0.9;
      transform: translateY(-1px);
    }
  `;
  document.head.appendChild(styleSheet);
}

export default MapManagement;
